import { useEffect, useRef } from 'react';

import { cx } from '../lib/format.js';

/**
 * Modal — an accessible dialog built on the native <dialog> element.
 *
 * Using <dialog> rather than a hand-rolled overlay means the browser supplies
 * focus trapping, the top layer, backdrop rendering and Escape-to-close. The only
 * behaviours added here are:
 *   • routing the native `cancel`/`close` events back to the caller's onClose
 *   • closing on a backdrop click, which <dialog> does not do by default
 *   • restoring page scroll while open
 */
export default function Modal({ open, onClose, title, description, children, footer, size = 'md' }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
      document.body.style.overflow = 'hidden';
    } else if (!open && dialog.open) {
      dialog.close();
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Ensure scroll is released if the component unmounts while still open.
  useEffect(() => () => {
    document.body.style.overflow = '';
  }, []);

  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-3xl' };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-description' : undefined}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      onClick={(event) => {
        // A click landing on the dialog element itself is a backdrop click;
        // clicks on the inner wrapper stop propagating below.
        if (event.target === dialogRef.current) onClose();
      }}
      className={cx(
        // `m-auto` is load-bearing: a native <dialog> centres itself via the UA's
        // `margin: auto`, and Tailwind's preflight resets margin to 0 — which is
        // what pins an unstyled dialog to the top-left corner. Restoring auto
        // margins re-centres it both axes without any fixed positioning.
        'm-auto w-[calc(100vw-2rem)] rounded-card border border-ink-200 bg-white p-0 shadow-lift',
        // The backdrop uses literal black rather than an ink token, because the
        // ink ramp inverts in dark mode and would produce a *light* scrim.
        'backdrop:bg-black/60 backdrop:backdrop-blur-sm',
        'open:animate-fade',
        sizes[size],
      )}
    >
      {/* Wrapper stops backdrop-click detection firing for interior clicks. */}
      <div onClick={(event) => event.stopPropagation()}>
        {(title || description) && (
          <header className="border-b border-ink-200 px-6 py-5">
            {title && (
              <h2 id="modal-title" className="text-lg font-bold tracking-tight text-ink-950">
                {title}
              </h2>
            )}
            {description && (
              <p id="modal-description" className="mt-1 text-sm leading-relaxed text-ink-600">
                {description}
              </p>
            )}
          </header>
        )}

        <div className="max-h-[65vh] overflow-y-auto px-6 py-5">{children}</div>

        {footer && <footer className="flex justify-end gap-3 border-t border-ink-200 px-6 py-4">{footer}</footer>}
      </div>
    </dialog>
  );
}
